import React, { useState, useContext, useCallback } from 'react';
import { Button, Input } from 'antd';
import { get, noop } from 'lodash';
import Share from './EditablePolicyShare';
import { IPolicyVersion, IResource, IPolicy } from '../../types';
import useShares from './hooks';
import { Error, SectionHeader, StyledModal } from './styled';
import EventContext from '../../EventContext';

interface IProps {
  version: IPolicyVersion;
  policy: IPolicy;
  visible?: boolean;
  resources: IResource[];
  onCancel?: () => void | typeof noop;
  confirmLoading?: boolean;
}

const EditModal: React.FC<IProps> = ({
  version,
  policy,
  resources,
  visible = false,
  onCancel = noop,
  confirmLoading = false,
}) => {
  const { updatePolicy } = useContext(EventContext);
  const [name, setName] = useState(get(version, 'name', ''));
  const [shares, sharesError, setShare, addShare, deleteShare] = useShares(
    JSON.parse(JSON.stringify(get(version, 'shares', []))),
  );
  const onOk = useCallback(() => {
    const payload = {
      id: policy.id,
      vid: version.id,
      name,
      shares,
    };
    updatePolicy({ policy: payload });
  }, [updatePolicy, JSON.stringify(shares), name]);

  const onNameChange = useCallback(
    (e) => {
      setName(e.target.value);
    },
    [setName],
  );

  const totalResources = shares.map((s) => s.resources.length).reduce((a, b) => a + b, 0);
  const modalContent = (
    <>
      <h3>Policy Name</h3>
      {!name && <Error>Name is required!</Error>}
      <Input placeholder="Policy Name" value={name} onChange={onNameChange} />
      <SectionHeader>Resources</SectionHeader>
      {sharesError && <Error>{sharesError}</Error>}
      {shares.map((share) => (
        <Share
          key={`${totalResources}/${share.id}`}
          share={share}
          resources={resources}
          allowDelete={shares.length > 1}
          onDelete={deleteShare}
          setShare={setShare}
        />
      ))}
      <Button onClick={addShare}>Add Resource</Button>
    </>
  );

  const disableSubmit =
    sharesError !== null ||
    !name ||
    (name === get(version, 'name', '') &&
      JSON.stringify(get(version, 'shares', [])) === JSON.stringify(shares)); // no change

  return (
    <StyledModal
      title="Edit Policy"
      visible={visible}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      onOk={onOk}
      okButtonProps={{
        disabled: disableSubmit,
      }}
      width="700px"
    >
      {modalContent}
    </StyledModal>
  );
};

export default EditModal;
